import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDealData } from './hooks/useDealData';
import VerdictHero from './VerdictHero';
import KpiRibbon from './KpiRibbon';
import ScoreBreakdown from './ScoreBreakdown';
import CostWaterfall from './CostWaterfall';
import MarketComparison from './MarketComparison';
import SignalsPanel from './SignalsPanel';
import InvestmentSummary from './InvestmentSummary';
import AiSidePanel from './AiSidePanel';
import SimulationView from './SimulationView';
import SectionDots from './SectionDots';

/* ── Topbar Tab-Toggle ── */
function ModeToggle({ mode, onModeChange }) {
  return (
    <div
      className="flex"
      style={{
        background: '#F3F3F5',
        borderRadius: 8,
        padding: 3,
        gap: 0,
      }}
    >
      {['verstehen', 'simulieren'].map((m) => (
        <button
          key={m}
          onClick={() => onModeChange(m)}
          className="text-[16px] font-medium transition-all duration-150"
          style={{
            padding: '6px 16px',
            borderRadius: 6,
            border: mode === m ? '1px solid #E0E0DF' : '1px solid transparent',
            background: mode === m ? '#FFFFFF' : 'transparent',
            color: mode === m ? '#1DB3B3' : '#838799',
            fontWeight: mode === m ? 600 : 500,
            cursor: 'pointer',
            lineHeight: '24px',
          }}
        >
          {m === 'verstehen' ? 'Analyse' : 'Simulation'}
        </button>
      ))}
    </div>
  );
}

/* ── Topbar ── */
function Topbar({ mode, onModeChange, dealName, onBack }) {
  return (
    <div
      className="sticky top-0 z-50 flex items-center justify-between"
      style={{
        height: 56,
        background: '#FFFFFF',
        borderBottom: '1px solid #E0E0DF',
        paddingLeft: 32,
        paddingRight: 32,
      }}
    >
      {/* Left: Zurück-Link + Deal-Name */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1 shrink-0 text-[14px] font-medium transition-colors"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#838799',
            padding: '4px 0',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#1DB3B3'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#838799'; }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M10 7H4M6.5 4L3.5 7l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Deals
        </button>
        <span style={{ color: '#E0E0DF', fontSize: 16 }}>|</span>
        <div className="text-[18px] font-semibold text-[#1F1F1F] truncate max-w-[280px]">
          {dealName || 'ImmoSight'}
        </div>
      </div>

      {/* Center: Tab-Toggle */}
      <ModeToggle mode={mode} onModeChange={onModeChange} />

      {/* Right: Deal speichern */}
      <button
        className="text-[16px] font-semibold transition-all duration-150"
        style={{
          background: '#FFFFFF',
          border: '1px solid #1DB3B3',
          color: '#1DB3B3',
          padding: '8px 16px',
          borderRadius: 8,
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#E8F7F7';
          e.currentTarget.style.borderColor = '#0E7A6B';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#FFFFFF';
          e.currentTarget.style.borderColor = '#1DB3B3';
        }}
      >
        Deal speichern
      </button>
    </div>
  );
}

export default function DealWorkspace() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [mode, setMode] = useState('verstehen');
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const { deal, raw, market, signals, baseline, subscores, recalc, isLoading, isError, userID } = useDealData(id);

  if (isLoading) return (
    <div className="min-h-screen bg-[#f8fafa] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#1DB3B3] border-t-transparent animate-spin mx-auto mb-3" />
        <p className="text-[13px] text-[#9A9AA0]">Objekt wird geladen…</p>
      </div>
    </div>
  );

  if (isError || !deal) return (
    <div className="min-h-screen bg-[#f8fafa] p-8">
      <button onClick={() => navigate('/properties')} className="text-[13px] text-[#9A9AA0] hover:text-[#1DB3B3] transition-colors mb-6">
        ← Zurück
      </button>
      <div className="text-center py-20">
        <div className="text-lg font-bold text-[#1F1F1F] mb-2">Objekt nicht gefunden</div>
        <p className="text-[13px] text-[#9A9AA0]">Das Listing existiert nicht oder wurde entfernt.</p>
      </div>
    </div>
  );

  const dealName = deal?.address || raw?.Address || '';

  return (
    <div className="min-h-screen bg-[#f8fafa]">
      {/* Sticky Topbar mit Tab-Toggle */}
      <Topbar mode={mode} onModeChange={setMode} dealName={dealName} onBack={() => navigate('/properties')} />

      {mode === 'verstehen' ? (
        <div className="max-w-[1200px] mx-auto">
          {/* [1] Hero: Score-Donut + Objektdaten + Action Buttons */}
          <div id="section-hero" className="px-8 pt-6">
            <VerdictHero
              baseline={baseline}
              signals={signals}
              market={market}
              subscores={subscores}
              scoreDetails={recalc?.scoreDetails}
              deal={deal}
              raw={raw}
              onOpenAi={() => setAiPanelOpen(true)}
            />
          </div>

          {/* [2] KPI-Ribbon */}
          <div id="section-kpis" className="px-8 mt-4">
            <KpiRibbon baseline={baseline} recalcData={recalc} />
          </div>

          {/* [3] Score-Aufschlüsselung */}
          <div id="section-score" className="px-8 mt-4">
            <ScoreBreakdown
              baseline={baseline}
              subscores={subscores}
              signals={signals}
              market={market}
            />
          </div>

          {/* [4]+[5] Two-Column: Ertragsrechnung + Marktvergleich */}
          <div id="section-kosten" className="px-8 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CostWaterfall baseline={baseline} />
              <div id="section-markt">
                <MarketComparison baseline={baseline} market={market} />
              </div>
            </div>
          </div>

          {/* [6] Signals */}
          <div id="section-signals" className="px-8 mt-4">
            <SignalsPanel signals={signals} />
          </div>

          {/* [7] Investment Summary */}
          <div id="section-summary" className="px-8 mt-4 pb-8">
            <InvestmentSummary
              baseline={baseline}
              signals={signals}
              market={market}
              subscores={subscores}
              onOpenAi={() => setAiPanelOpen(true)}
            />
          </div>

          <SectionDots />
        </div>
      ) : (
        <div className="max-w-[1200px] mx-auto">
          <SimulationView
            baseline={baseline}
            subscores={subscores}
            market={market}
            onSwitchToVerstehen={() => setMode('verstehen')}
            userID={userID}
            listingId={id}
          />
        </div>
      )}

      <AiSidePanel
        isOpen={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        baseline={baseline}
        signals={signals}
        market={market}
        subscores={subscores}
        listingId={id}
      />
    </div>
  );
}
